import React, { useMemo, useState } from 'react'
import { FormField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { EmailPattern } from '@/utils/formValidation'

export const ForgotPasswordDialog: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen = true, onClose }) => {
  const [wasSubmitted, setSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const identityApiClient = useApiClient(IdentityApiClient)

  const handleForgotPassword = async () => {
    try {
      setIsLoading(true)
      await identityApiClient.requestPasswordReset({ email })
      setSubmitted(true)
    } finally {
      setIsLoading(false)
    }
  }

  const error = useMemo(() => {
    if (!email) return 'Email is Required'
    else if (!EmailPattern.test(email)) return 'Email is Invalid'
  }, [email])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        setSubmitted(false)
        onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Forgot password</DialogTitle>
          <DialogDescription>
            {wasSubmitted
              ? "We've sent an email with further instructions. Please check your inbox."
              : "Enter your email address below and we'll send you a link to reset your password."}
          </DialogDescription>
        </DialogHeader>
        {!wasSubmitted && (
          <FormField
            label='Email'
            placeholder='Enter email address'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
        <DialogFooter>
          <DialogClose
            render={<Button variant='outline'>{wasSubmitted ? 'Close' : 'Cancel'}</Button>}
          />
          {!wasSubmitted && (
            <Tooltip disabled={!error}>
              <TooltipTrigger>
                <Button onClick={handleForgotPassword} disabled={isLoading || !!error}>
                  Reset Password
                  {isLoading && <Spinner />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>{error}</TooltipContent>
            </Tooltip>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
